/**
 * The mount glue each host document runs before any surface mounts, moved from the shell's
 * `boot/runtime.ts` and `boot/import-map.ts` (kernel apps, WS-14 section 3.6; layered shell
 * surfaces, D4, D23, R-2, R-5, R-27, WS-6 sections 3.1 and 3.2).
 *
 * A host never imports `@zion/sdk-surfaces/v1` statically: the one copy the import map serves is
 * the one every surface links to, so the document holds one host slot and one mount counter. The
 * manifest read runs before any import map exists, so it bundles `@zion/sdk-surfaces/importmap`
 * (which imports nothing) and holds no second shape, error class or URL rule of its own.
 */
import { parseImportMapManifest, type ImportMap } from '@zion/sdk-surfaces/importmap';
import type * as SurfacesV1 from '@zion/sdk-surfaces/v1';

/** The three runtime functions a host calls (R-27). */
export type SurfacesRuntime = Pick<typeof SurfacesV1, 'installSurfaceHost' | 'hasSurfaceHost' | 'mountSurface'>;

/** The dynamic `import()` seam: a URL in, the module namespace out. Production binds a `@vite-ignore` import. */
export type ModuleImporter = (url: string) => Promise<Readonly<Record<string, unknown>>>;

/** Thrown when the runtime chunk could not be imported or exports the wrong shape. */
export class RuntimeLoadError extends Error {
  override readonly name = 'RuntimeLoadError';
  /** The URL the host tried. */
  readonly url: string;

  /**
   * @param url The runtime URL.
   * @param cause The import's rejection, or the `TypeError` that names the missing functions.
   */
  constructor(url: string, cause: unknown) {
    super(`the surface runtime at ${url} did not load`, { cause });
    this.url = url;
  }
}

/** True when `value` is callable. A guard, so the namespace members narrow with no cast. */
function isFunction(value: unknown): value is (...args: never[]) => unknown {
  return typeof value === 'function';
}

/**
 * Import `url`, the value of `runtimeUrl(map)` from `@zion/sdk-surfaces/importmap`, once.
 *
 * @param url The runtime chunk URL.
 * @param importModule The import seam.
 * @returns The three functions when `installSurfaceHost`, `hasSurfaceHost` and `mountSurface` are
 *   all functions on the namespace.
 * @throws {@link RuntimeLoadError} carrying `url`, as a rejection, when the import rejects (the
 *   import's error is the `cause`) or the namespace lacks any of the three. No retry.
 */
export async function loadSurfacesRuntime(url: string, importModule: ModuleImporter): Promise<SurfacesRuntime> {
  let mod: Readonly<Record<string, unknown>>;
  try {
    mod = await importModule(url);
  } catch (err) {
    throw new RuntimeLoadError(url, err);
  }
  const { installSurfaceHost, hasSurfaceHost, mountSurface } = mod;
  if (!isFunction(installSurfaceHost) || !isFunction(hasSurfaceHost) || !isFunction(mountSurface)) {
    throw new RuntimeLoadError(url, new TypeError('the runtime namespace lacks installSurfaceHost, hasSurfaceHost or mountSurface'));
  }
  // The import map serves the one runtime chunk, whose namespace is `@zion/sdk-surfaces/v1` itself;
  // the guard above proved each member callable, and the signatures are the contract's.
  const runtime: SurfacesRuntime = {
    installSurfaceHost: installSurfaceHost as SurfacesRuntime['installSurfaceHost'],
    hasSurfaceHost: hasSurfaceHost as SurfacesRuntime['hasSurfaceHost'],
    mountSurface: mountSurface as SurfacesRuntime['mountSurface'],
  };
  return runtime;
}

/** Where nginx serves the manifest the build writes (`location = /importmap/manifest.json`). */
export const IMPORT_MAP_MANIFEST_URL = '/importmap/manifest.json';

/** The React adapter's bare specifier; a host imports it by the map's URL for legacy bundles (D28). */
export const REACT_ADAPTER_SPECIFIER = '@zion/sdk-surfaces-react/v1';

/** How long the manifest request may take before the host renders its fatal screen. */
export const IMPORT_MAP_FETCH_TIMEOUT_MS = 10_000;

/** Why the manifest could not be read: no 2xx answer, or a body that is not JSON. */
export type ManifestFetchFailure = 'unreachable' | 'not-json';

/** Thrown by {@link loadImportMapManifest} before the parser runs. */
export class ManifestFetchError extends Error {
  override readonly name = 'ManifestFetchError';
  /** The closed reason. */
  readonly reason: ManifestFetchFailure;

  /** @param reason The closed reason; the message is `the import-map manifest is <reason>`. */
  constructor(reason: ManifestFetchFailure) {
    super(`the import-map manifest is ${reason}`);
    this.reason = reason;
  }
}

/**
 * Read and validate the manifest.
 *
 * Sends one `GET` {@link IMPORT_MAP_MANIFEST_URL} through `fetcher` with `cache: 'no-cache'` and a
 * {@link IMPORT_MAP_FETCH_TIMEOUT_MS} timeout signal. The global `fetch` serves: the manifest is a
 * static file and carries no bearer.
 *
 * @returns The frozen map from `parseImportMapManifest`.
 * @throws As a rejection: {@link ManifestFetchError} `unreachable` for a network error, a timeout
 *   or a non-2xx status; `not-json` for a body `JSON.parse` refuses; the parser's `ImportMapError`
 *   unchanged for a body outside the import-map rules. No retry, no other side effect.
 */
export async function loadImportMapManifest(fetcher: typeof fetch): Promise<ImportMap> {
  let res: Response;
  try {
    res = await fetcher(IMPORT_MAP_MANIFEST_URL, {
      cache: 'no-cache',
      signal: AbortSignal.timeout(IMPORT_MAP_FETCH_TIMEOUT_MS),
    });
  } catch {
    throw new ManifestFetchError('unreachable');
  }
  if (!res.ok) throw new ManifestFetchError('unreachable');
  let body: unknown;
  try {
    body = JSON.parse(await res.text());
  } catch {
    throw new ManifestFetchError('not-json');
  }
  return parseImportMapManifest(body);
}
